import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controlled as CodeMirrorComponent } from 'react-codemirror2';

import 'codemirror/mode/javascript/javascript';

import Button from 'components/Button';
import { useI18nContext } from 'components/I18nProvider';
import { Stack } from 'components/layout';
import { post } from 'utils/AjaxUtil';
import DataUrlConstants from 'utils/DataUrlConstants';
import { base64EncodeUtf8 } from 'utils/StringUtil';

export interface SelectWinnerTestResult {
  winner: Record<string, unknown>;
  losers: Record<string, unknown>[];
}

export interface SelectWinnerTestInputs {
  incoming: string;
  duplicates: string;
}

export interface MergePolicyTestInputs {
  winner: string;
  losers: string;
}

export type TestInputs = SelectWinnerTestInputs | MergePolicyTestInputs;

export interface PythonCodeTestPanelProps {
  code: string;
  isSelectWinner: boolean;
  onSelectWinnerTestResult?: (result: SelectWinnerTestResult) => void;
  selectWinnerTestResult?: SelectWinnerTestResult;
  savedTestInputs?: TestInputs;
  onTestInputsChange?: (inputs: TestInputs) => void;
}

async function executeCustomCode(
  code: string,
  inputs: Record<string, unknown>
): Promise<{ ok: boolean; result?: unknown; error?: string; logs?: string }> {
  const response = await post(DataUrlConstants.PYBOX_RUN, { code: base64EncodeUtf8(code), inputs });
  return response.data;
}

// The three Account records used across both screens
const REC_001 = {
  id: 'rec_001',
  name: 'Acme Corp',
  annual_revenue: 5000000,
  industry: 'Technology',
  address: { street: '100 Main St', city: 'New York', state: 'NY', zip: '10001', country: 'USA' },
  lastModified: '2024-02-28T10:30:00Z',
};

const REC_002 = {
  id: 'rec_002',
  name: 'Acme Corporation',
  annual_revenue: 5200000,
  industry: 'Technology',
  address: { street: '200 Market St', city: 'San Francisco', state: 'CA', zip: '', country: '' },
  lastModified: '2024-03-20T14:00:00Z',
};

const REC_003 = {
  id: 'rec_003',
  name: 'ACME Corp.',
  annual_revenue: 4800000,
  industry: 'Technology',
  address: { street: '', city: 'Chicago', state: 'IL', zip: '60601', country: 'USA' },
  lastModified: '2024-01-15T09:15:00Z',
};

// Select Winner: incoming + duplicates → code picks rec_002 (city = "San Francisco")
const EXAMPLE_INCOMING = JSON.stringify(REC_001, null, 2);
const EXAMPLE_DUPLICATES = JSON.stringify([REC_002, REC_003], null, 2);

// Merge Policy: winner = rec_002 (selected above), losers = the rest
const EXAMPLE_WINNER = JSON.stringify(REC_002, null, 2);
const EXAMPLE_LOSERS = JSON.stringify([REC_001, REC_003], null, 2);

const PythonCodeTestPanel = ({
  code,
  isSelectWinner,
  onSelectWinnerTestResult,
  selectWinnerTestResult,
  savedTestInputs,
  onTestInputsChange,
}: PythonCodeTestPanelProps) => {
  const { tc } = useI18nContext();

  const key1 = isSelectWinner ? 'incoming' : 'winner';
  const key2 = isSelectWinner ? 'duplicates' : 'losers';
  const var1Label = isSelectWinner ? 'incoming (dict)' : 'winner (dict)';
  const var2Label = isSelectWinner ? 'duplicates (List[dict])' : 'losers (List[dict])';

  // For merge policy: fresh test result from this session takes priority over saved inputs
  const defaultVar1 = isSelectWinner
    ? (savedTestInputs as any)?.[key1] ?? EXAMPLE_INCOMING
    : selectWinnerTestResult
    ? JSON.stringify(selectWinnerTestResult.winner, null, 2)
    : (savedTestInputs as any)?.[key1] ?? EXAMPLE_WINNER;
  const defaultVar2 = isSelectWinner
    ? (savedTestInputs as any)?.[key2] ?? EXAMPLE_DUPLICATES
    : selectWinnerTestResult
    ? JSON.stringify(selectWinnerTestResult.losers, null, 2)
    : (savedTestInputs as any)?.[key2] ?? EXAMPLE_LOSERS;

  // Determine whether we're starting with example data (no saved inputs and no test result override)
  const isUsingExampleData = isSelectWinner
    ? !(savedTestInputs as any)?.[key1]
    : !selectWinnerTestResult && !(savedTestInputs as any)?.[key1];

  const [var1Value, setVar1ValueRaw] = useState(defaultVar1);
  const [var2Value, setVar2ValueRaw] = useState(defaultVar2);
  const var1Ref = useRef(defaultVar1);
  const var2Ref = useRef(defaultVar2);
  const [showExampleHint, setShowExampleHint] = useState(isUsingExampleData);
  const [resultValue, setResultValue] = useState('');
  const [logsValue, setLogsValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Notify parent of initial values on mount so they're included in the save payload
  useEffect(() => {
    if (!savedTestInputs || (!isSelectWinner && selectWinnerTestResult)) {
      onTestInputsChange?.(({ [key1]: defaultVar1, [key2]: defaultVar2 } as unknown) as TestInputs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setVar1Value = useCallback(
    (v: string) => {
      setVar1ValueRaw(v);
      var1Ref.current = v;
      setShowExampleHint(false);
      onTestInputsChange?.(({ [key1]: v, [key2]: var2Ref.current } as unknown) as TestInputs);
    },
    [key1, key2, onTestInputsChange]
  );

  const setVar2Value = useCallback(
    (v: string) => {
      setVar2ValueRaw(v);
      var2Ref.current = v;
      setShowExampleHint(false);
      onTestInputsChange?.(({ [key1]: var1Ref.current, [key2]: v } as unknown) as TestInputs);
    },
    [key1, key2, onTestInputsChange]
  );

  // Update Merge Policy inputs when Select Winner result changes
  const prevResultRef = useRef(selectWinnerTestResult);
  useEffect(() => {
    if (!isSelectWinner && selectWinnerTestResult && selectWinnerTestResult !== prevResultRef.current) {
      const newVar1 = JSON.stringify(selectWinnerTestResult.winner, null, 2);
      const newVar2 = JSON.stringify(selectWinnerTestResult.losers, null, 2);
      setVar1ValueRaw(newVar1);
      setVar2ValueRaw(newVar2);
      var1Ref.current = newVar1;
      var2Ref.current = newVar2;
      onTestInputsChange?.(({ [key1]: newVar1, [key2]: newVar2 } as unknown) as TestInputs);
      prevResultRef.current = selectWinnerTestResult;
    }
  }, [isSelectWinner, selectWinnerTestResult, onTestInputsChange, key1, key2]);

  const jsonEditorOptions = useMemo(
    () => ({
      mode: { name: 'javascript', json: true },
      lineNumbers: true,
      lineWrapping: true,
      matchBrackets: true,
      autoCloseBrackets: true,
    }),
    []
  );

  const resultOptions = useMemo(
    () => ({
      mode: { name: 'javascript', json: true },
      lineNumbers: true,
      lineWrapping: true,
      readOnly: true,
    }),
    []
  );

  const logsOptions = useMemo(
    () => ({
      mode: 'text/plain',
      lineNumbers: true,
      lineWrapping: true,
      readOnly: true,
    }),
    []
  );

  const handleRunTest = useCallback(async () => {
    setError('');
    setResultValue('');
    setLogsValue('');

    let parsed1: unknown;
    let parsed2: unknown;

    try {
      parsed1 = JSON.parse(var1Value);
    } catch {
      setError(`${var1Label}: ${tc('invalid_json')}`);
      return;
    }

    try {
      parsed2 = JSON.parse(var2Value);
    } catch {
      setError(`${var2Label}: ${tc('invalid_json')}`);
      return;
    }

    const varName1 = isSelectWinner ? 'incoming' : 'winner';
    const varName2 = isSelectWinner ? 'duplicates' : 'losers';

    setLoading(true);
    try {
      const response = await executeCustomCode(code, {
        [varName1]: parsed1,
        [varName2]: parsed2,
      });
      if (response.logs) {
        setLogsValue(response.logs);
      }
      if (response.ok) {
        setResultValue(JSON.stringify(response.result, null, 2));
        if (isSelectWinner && onSelectWinnerTestResult) {
          const allRecords = [parsed1 as Record<string, unknown>, ...(parsed2 as Record<string, unknown>[])];
          const winnerId = response.result;
          const winnerRecord = allRecords.find((r) => r.id === winnerId);
          if (winnerRecord) {
            onSelectWinnerTestResult({
              winner: winnerRecord,
              losers: allRecords.filter((r) => r.id !== winnerId),
            });
          }
        }
      } else {
        setError(response.error || tc('execution_failed'));
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || tc('unknown_error'));
    } finally {
      setLoading(false);
    }
  }, [code, var1Value, var2Value, var1Label, var2Label, isSelectWinner, onSelectWinnerTestResult, tc]);

  return (
    <Stack className="python-code-test-panel" spacing="md">
      {showExampleHint && <div className="test-panel-example-hint">{tc('example_data_hint')}</div>}
      <div className="test-panel-input-row">
        <div className="test-panel-input">
          <label className="test-panel-label">{var1Label}</label>
          <CodeMirrorComponent
            value={var1Value}
            options={jsonEditorOptions}
            onBeforeChange={(_editor, _data, newValue) => setVar1Value(newValue)}
          />
        </div>
        <div className="test-panel-input">
          <label className="test-panel-label">{var2Label}</label>
          <CodeMirrorComponent
            value={var2Value}
            options={jsonEditorOptions}
            onBeforeChange={(_editor, _data, newValue) => setVar2Value(newValue)}
          />
        </div>
      </div>

      {error && <div className="test-panel-error">{error}</div>}

      <Button type="primary" loading={loading} onClick={handleRunTest}>
        {tc('run_test')}
      </Button>

      <div className="test-panel-output-row">
        <div className="test-panel-result">
          <label className="test-panel-label">
            {isSelectWinner ? tc('selected_winner_record_id') : tc('merged_record')}
          </label>
          <CodeMirrorComponent value={resultValue} options={resultOptions} onBeforeChange={() => {}} />
        </div>
        <div className="test-panel-result">
          <label className="test-panel-label">{tc('logs')}</label>
          <CodeMirrorComponent value={logsValue} options={logsOptions} onBeforeChange={() => {}} />
        </div>
      </div>
    </Stack>
  );
};

export default PythonCodeTestPanel;
